import {
  CollaborationAccessEnum,
  IQuoteRespSchema,
  IQuoteWithBillGroup,
  QuoteStatusEnum,
} from '@monetize/types/app';
import {
  ApiListResponse,
  FilterOptionType,
  FilterType,
  FilterTypeOperator,
  TDataTablePager,
} from '@monetize/types/common';
import { arrayToObject } from '@monetize/utils/core';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useAtom } from 'jotai';
import { useGetAllBillGroups } from '../../api/billGroupService';
import { useGetQuoteList } from '../../api/cpqService';
import { useGetListData } from '../../api/queryUtils';
import { buildHideTestAccountFilterOption } from '../../components/Monetize/DataTable/FilterOptions/HideTestAccountFilterOption';
import { ToggleFilterOptionContent } from '../../components/Monetize/DataTable/FilterOptions/ToggleFilterOptionContent';
import MEmptyDataPlaceholder from '../../components/Monetize/MEmptyDataPlaceholder';
import { QuoteActionsV2 } from '../../components/Quotes/QuoteActionsV2';
import { ROUTES } from '../../constants';
import { useCanCreateQuote } from '../../routes/Quotes/Quote/useCanCreateQuote';
import { useDocumentHead } from '../../services/documentHead';
import {
  tableFilterSelector,
  tablePagerSelector,
  tableSearchTermSelector,
} from '../../store/global.store';
import { FilterStateKeys, getDefaultPager } from '../../store/store.types';
import {
  buildFilterParamsRequestObject,
  transformTableFilterValue,
} from '../../utils';
import { editQuote, openQuote, reviewQuote } from '../../utils/quotes';

/**
 * Get table data for quotes
 * @param accountId If provided, quotes for the specific account will be displayed instead of all quotes
 * @param enabled Defaults to true, passed to the underlying useQuery hook
 * @returns
 */
export const useQuotesTableData = ({
  accountId,
  billGroupId,
  enabled = true,
  filterKey = FilterStateKeys.QUOTE_LIST,
}: {
  accountId?: string;
  billGroupId?: string;
  enabled?: boolean;
  filterKey?: string;
} = {}) => {
  const navigate = useNavigate();

  const canCreateQuote = useCanCreateQuote();
  // Account scoped quotes filters are not stored in sessionStorage
  const persistFilters = !accountId && enabled;
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(filterKey),
  );

  const [pager, setPager] = useAtom(tablePagerSelector(filterKey));

  const [initialSearchTerm, persistSearchTerm] = useAtom(
    tableSearchTermSelector(filterKey),
  );

  const [filters, setFilters] = useState<FilterType[]>(
    persistFilters ? initialFilters : [],
  );

  const [searchKey] = useState<string>('description');
  const [searchTerm, setSearchTerm] = useState<string>(
    persistFilters ? initialSearchTerm : '',
  );

  const filterParams = useMemo(
    () =>
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        searchKey,
      ),
    [filters, searchTerm, searchKey],
  );

  const [quotesWithBillGroup, setQuotesWithBillGroup] =
    useState<ApiListResponse<IQuoteWithBillGroup>>();

  const presencePager: TDataTablePager = {
    // for determining whether any records exist
    first: 0,
    rows: 1,
    page: 0,
  };

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  useEffect(() => {
    persistSearchTerm(searchTerm);
  }, [searchTerm, persistSearchTerm]);

  const {
    isLoading: isBillGroupResLoading,
    isFetching: isBillGroupResFetching,
    data: billGroupsById,
  } = useGetAllBillGroups(
    { filters: { accountId: accountId! } },
    {
      enabled: !!accountId,
      select: (data) => arrayToObject(data, 'id'),
    },
  );

  const { data: guidedQuotingData } = useGetListData(
    'guidedQuoting_v2',
    {
      config: { rows: 1, page: 0 },
      filters: { status: 'ACTIVE' },
    },
    {
      gcTime: 1000 * 60 * 10, // 10 min
      staleTime: 1000 * 60 * 3, // 3 min
    },
  );

  const {
    isLoading,
    isRefetching,
    isFetched,
    refetch,
    data: quoteList,
    isError,
    error,
  } = useGetQuoteList(
    {
      config: pager,
      filters: billGroupId ? { ...filterParams, billGroupId } : filterParams,
      accountId,
    },
    {
      enabled,
    },
  );

  const { data: isOfferingAvailable } = useGetListData(
    'productCatalogOfferings',
    { config: presencePager },
    { select: (value) => value.totalElements > 0 },
  );

  const { data: isProductAvailable } = useGetListData(
    'productCatalogProducts',
    { config: presencePager },
    { select: (value) => value.totalElements > 0 },
  );

  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Quotes');
  }, []);

  useEffect(() => {
    if (quoteList && billGroupsById) {
      setQuotesWithBillGroup(() => {
        return {
          ...quoteList,
          content: quoteList.content.map((quote) => {
            return {
              ...quote,
              billGroup: quote.billGroupId
                ? billGroupsById[quote.billGroupId]
                : undefined,
            };
          }),
        };
      });
    }
  }, [quoteList, billGroupsById]);

  const filterOptions: FilterOptionType[] = [];

  if (!accountId) {
    filterOptions.push({
      title: '',
      key: 'collaborationAccess',
      operator: FilterTypeOperator.TOGGLE,
      renderOptionContent: (props) => (
        <ToggleFilterOptionContent
          {...props}
          label="Show quotes shared with me"
        />
      ),
      options: {
        trueValue: `in:${CollaborationAccessEnum.VIEW},${CollaborationAccessEnum.EDIT}`,
      },
    });
    filterOptions.push(
      buildHideTestAccountFilterOption('Hide Test Account Quotes'),
    );
  }

  const actionBodyTemplate = useCallback(
    (data: IQuoteRespSchema | IQuoteWithBillGroup): ReactNode => (
      <QuoteActionsV2
        setQuote={() => refetch()}
        quote={data}
        loadData={refetch}
        isQuotesList
        resetQuoteForm={() => {}}
        onSendQuote={() => {}}
      />
    ),
    [refetch],
  );

  const onResetFilter = () => {
    setFilters([]);
    setPager(getDefaultPager(filterKey));
  };

  const handleCreateQuotes = () => {
    navigate(ROUTES.QUOTE_NEW);
  };

  const handleEditQuote = (quoteId: string, openInNewWindow = false) => {
    editQuote(navigate, quoteId, openInNewWindow);
  };

  const handleReviewQuote = (quoteId: string, openInNewWindow = false) => {
    reviewQuote(navigate, quoteId, openInNewWindow);
  };

  const handleOpenQuote = (
    quoteId: string,
    status: QuoteStatusEnum,
    openInNewWindow = false,
  ) => {
    openQuote(navigate, quoteId, status, openInNewWindow);
  };

  const EmptyQuotesPlaceholder: FC = () => {
    let message = '';
    let route = ROUTES.QUOTE_NEW;
    let btnLabel = canCreateQuote ? 'New Quote' : null;
    if (isProductAvailable === false) {
      message =
        'It seems that you have not set up any products. You need to add products, then offerings, to get a quote started.';
      route = ROUTES.PRODUCT_NEW;
      btnLabel = 'New Product';
    } else if (isOfferingAvailable === false) {
      message =
        'It seems that you have not set up any offerings. You need to add offerings to get a quote started.';
      route = ROUTES.OFFERING_NEW;
      btnLabel = 'New Offering';
    }
    return (
      <MEmptyDataPlaceholder
        mainMessage="Looks like there are no quotes here."
        smallMessage={message}
        btnLabel={btnLabel}
        to={route}
        alternateBtnLabel={
          canCreateQuote && guidedQuotingData?.totalElements === 1
            ? 'New Guided Quote'
            : null
        }
        alternateTo={
          guidedQuotingData?.totalElements === 1
            ? ROUTES.QUOTE_NEW_GUIDED
            : undefined
        }
      />
    );
  };

  return {
    quoteList: accountId ? quotesWithBillGroup : quoteList,
    loading:
      isLoading ||
      (isRefetching && !isFetched) ||
      (isBillGroupResFetching && isBillGroupResLoading),
    searchKey,
    searchTerm,
    setSearchTerm,
    pager,
    setPager,
    actionBodyTemplate,
    handleCreateQuotes,
    handleEditQuote,
    handleReviewQuote,
    handleOpenQuote,
    filters,
    filterOptions,
    setFilters,
    onResetFilter,
    EmptyQuotesPlaceholder,
    isGuidedQuotingAvailable: guidedQuotingData?.totalElements === 1,
    isError,
    refetch,
    error,
  };
};
